<template>
  <div
    class="m-table"
  >
    <slot name="after-table" />
    <div
      ref="table-sticky-trigger"
      class="m-table__sticky-trigger"
      :style="stickyTriggerStyle"
    />
    <table
      ref="table-el"
      :class="{
        'm-table_sticky': isSticky,
        'm-table_with-summary': withSummaryRow,
        'm-table_with-header-word-wrap': allowHeaderWordWrap
      }"
      :style="cssVars"
      class="m-table__body"
    >
      <thead>
        <slot name="table-header" />
      </thead>
      <tbody>
        <slot name="table-content" />
      </tbody>
    </table>
  </div>
</template>

<script>
  import nextFrameCreateObserver from 'utils/observers/nextFrameCreateObserver';
  import getCssVarNameForColumn from 'components/Data/Tables/MTable/utils/getCssVarNameForColumn';
  import nextFrame from 'utils/nextFrame';

  export default {
    name: 'MTableBody',
    props: {
      withSummaryRow: {
        type: Boolean,
        default: false
      },
      allowHeaderWordWrap: {
        type: Boolean,
        default: false
      },
      columnsFlat: {
        type: Array,
        required: true
      },
      columnParams: {
        type: Object,
        default: Object
      },
      superHeaderNodeKeys: {
        type: Array,
        default: Array
      }
    },
    data () {
      return {
        isSticky: false,
        tableWidth: null
      };
    },
    computed: {
      stickyTriggerStyle () {
        return {
          width: this.tableWidth ? `${this.tableWidth}px` : 'auto'
        };
      },
      cssVars () {
        const vars = {};

        const columnsKeys = [
          ...this.columnsFlat.map(c => c.itemKey),
          ...this.superHeaderNodeKeys
        ];

        columnsKeys.forEach(c => {
          let columnWidth = 'auto';
          if (this.columnParams[c] && this.columnParams[c].width) {
            columnWidth = `${this.columnParams[c].width}px`;
          }
          vars[getCssVarNameForColumn(c, 'width')] = columnWidth;
        });
        return vars;
      }
    },
    async mounted () {
      const {observerDestructor} = await nextFrameCreateObserver(
        'IntersectionObserver',
        () => this.$refs['table-sticky-trigger'],
        entries => {
          this.handleStickyChange(!entries[0].isIntersecting);
        },
        {
          root: this.$el,
          threshold: 1.0
        }
      );
      this.$on('hook:beforeDestroy', () => {
        observerDestructor();
      });
    },
    updated () {
      nextFrame(() => {
        this.updateTableWidth();
      });
    },
    methods: {
      handleStickyChange (active) {
        this.isSticky = active;
      },
      updateTableWidth () {
        if (this.$refs['table-el']) {
          this.tableWidth = this.$refs['table-el'].offsetWidth;
        }
      }
    }
  };
</script>

<style>
  .m-table {
    --m-table-font-size-s: 12px;
    --m-table-font-size-m: 14px;

    overflow: auto;

    max-height: 650px;

    padding-bottom: 6px;

    box-shadow: var(--floating-card-shadow);

    border-radius: 4px;

    /* hack to prevent scroll from lagging */
    transform: translateZ(0);

    &.m-table-font-size-s {
      font-size: var(--m-table-font-size-s);
    }

    &.m-table-font-size-m {
      font-size: var(--m-table-font-size-m);
    }
  }

  .m-table__body {
    color: var(--text-main-color);

    border-spacing: 0;
    border-collapse: initial;
  }

  .m-table__body th,
  .m-table__body td {
    padding: 0;
  }

  .m-table_with-summary .m-table-row__first * {
    font-style: italic;
    font-weight: var(--bold-font-weight);
  }

  .m-table__sticky-trigger {
    width: var(--table-width);
  }
</style>
